import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach the current access token to every request.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Single-flight refresh state ──────────────────────────────────────────
// When the access token expires, the page often has 5-10 requests in
// flight simultaneously (Media Lab mount fires /assets, /objects,
// /generations, /user, /conversations, /models, etc. in parallel). Each
// returns 401 and the *previous* implementation tried to refresh from each
// 401 handler concurrently — refresh tokens are single-use and rotated on
// success, so the first refresh wins and the rest fail because they're
// hitting the API with a now-invalidated token. The page would end up
// with ~1 successful request and the rest permanently rejected.
//
// Single-flight fix: at most ONE refresh in flight at a time. All
// concurrent 401s wait on the same in-flight refresh promise; when it
// resolves, every queued request retries with the freshly-issued access
// token. If the refresh fails, every queued request rejects with the
// original error.
let refreshInFlight = null;

const performRefresh = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');
  // Use a bare axios call (not the `api` instance) so this request
  // doesn't loop back through our interceptor on its own 401.
  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  const tokens = response.data?.data?.tokens || {};
  const accessToken = tokens.accessToken;
  const newRefreshToken = tokens.refreshToken;
  if (!accessToken) throw new Error('Refresh response missing accessToken');
  localStorage.setItem('accessToken', accessToken);
  if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
  return accessToken;
};

// Public landing-page paths — these get to keep showing themselves even if
// some optional API call (e.g. /billing/founding-spots for the live counter)
// returns 401. Without this gate, every anonymous visitor on the public site
// got force-redirected to /login the moment the landing page mounted and
// fired a request the gateway considered protected.
const PUBLIC_PATHS = ['/', '/terms', '/privacy', '/login', '/signup', '/forgot-password', '/reset-password'];
const isOnPublicPath = () => {
  const p = window.location.pathname;
  return PUBLIC_PATHS.some(pp => p === pp || p.startsWith(pp + '/'));
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  // Don't redirect anonymous visitors on public pages — a 401 on the
  // founding-spots banner or any other public-page API call is expected,
  // not a session-expired event. Only kick users back to /login when
  // they're inside the protected app shell.
  if (isOnPublicPath()) return;
  if (!window.location.pathname.includes('/login')) {
    // Use replace so the back button doesn't bounce the user into a
    // half-loaded protected page.
    window.location.replace('/login');
  }
};

// Response interceptor — handle expired access tokens transparently.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Skip refresh for auth endpoints (they aren't 401-on-expiry; they're
    // 401 on bad credentials, and refreshing wouldn't help) and for the
    // refresh endpoint itself (would infinite-loop).
    const url = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/auth/');

    // No refresh token stored = anonymous visitor (or session already
    // cleared). Skip the refresh dance entirely and just reject; entering
    // the refresh path would call performRefresh, fail with "no refresh
    // token", and trip clearAuthAndRedirect for someone who never had a
    // session to begin with.
    const hasRefreshToken = !!localStorage.getItem('refreshToken');
    if (status !== 401 || originalRequest._retry || isAuthEndpoint || !hasRefreshToken) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Start a refresh if one isn't already running. All concurrent 401s
    // share the same promise, so only one /auth/refresh call ever fires.
    if (!refreshInFlight) {
      refreshInFlight = performRefresh()
        .catch((err) => {
          // Surface the failure to every queued request, then clear auth.
          clearAuthAndRedirect();
          throw err;
        })
        .finally(() => {
          // Reset so the NEXT 401 (after this batch retries) starts a
          // fresh refresh — important if the new tokens themselves expire
          // later in the session.
          refreshInFlight = null;
        });
    }

    try {
      const accessToken = await refreshInFlight;
      // Re-issue the original request with the new token. Header has to
      // be updated explicitly because the request interceptor already ran
      // when the original was created.
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return api(originalRequest);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }
);

export default api;
